import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Row, Col } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import DocumentUpload from '../../components/DocumentUpload';
import axios from 'axios';
import apiConfig from '../credentials/apiConfig';
import { toast } from 'react-toastify';
import image from '../../images/ai-robot.gif';
import ReactTextTransition from "react-text-transition";
import './index.scss';
import DownloadSampleFile from '../../components/DownloadSampleFile';
function EditAnalysisPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState({
    clientName: '',
    analysisYear: '',
    currentYearInvestmentSchedule: null,
    priorYearInvestmentSchedule: null,
    priorYearFinancials: null
  });
  const [existingFiles, setExistingFiles] = useState({
    currentYearInvestmentSchedule: null,
    priorYearInvestmentSchedule: null,
    priorYearFinancials: null
  });
  const [removedFiles, setRemovedFiles] = useState({
    currentYearInvestmentSchedule: false,
    priorYearInvestmentSchedule: false,
    priorYearFinancials: false
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientsError, setClientsError] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [engagementId, setEngagementId] = useState("");
  const [status, setStatus] = useState("");
  const [showLoadingPopup, setShowLoadingPopup] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [extraMessage, setExtraMessage] = useState("");

  const analysisYears = [
    '2024',
    '2023',
    '2022',
    '2021',
    '2020'
  ];

  const initialMessages = [
    "AI is analyzing your investment schedule. Please wait...",
    "Hang tight! Your analysis is in progress..."
  ];

  const repeatMessages = [
    "Fetching your results. Almost there...",
    "Great things take time. Preparing your results now...",
    "Just a little longer. Thanks for your patience!"
  ];

  // Fetch clients and investment schedule data when component mounts
  useEffect(() => {
    fetchClients();
    fetchInvestmentSchedule();
  }, [id]);

  // Polling message timer effects
  useEffect(() => {
    let messageTimer, elapsedTimer;

    if (isPolling) {
      setCurrentMessageIndex(0);
      setElapsedSeconds(0);
      setExtraMessage("");

      messageTimer = setInterval(() => {
        setCurrentMessageIndex(prev => prev + 1);
      }, 5000);

      elapsedTimer = setInterval(() => {
        setElapsedSeconds(prev => prev + 1);
      }, 1000);
    }

    return () => {
      clearInterval(messageTimer);
      clearInterval(elapsedTimer);
    };
  }, [isPolling]);

  useEffect(() => {
    if (elapsedSeconds >= 300) {
      setExtraMessage("Thanks for your patience! Almost done.");
    } else if (elapsedSeconds >= 180) {
      setExtraMessage("Almost there. Please don't close this tab.");
    } else if (elapsedSeconds >= 60) {
      setExtraMessage("Still working... Some files may take longer.");
    }
  }, [elapsedSeconds]);

  // Polling functionality for investment schedule mapping
  useEffect(() => {
    if (!engagementId) return;

    let pollingInterval = null;

    const fetchStatusAndProcess = async () => {
      try {
        const authToken = localStorage.getItem("authToken");
        const res = await axios.get(
          `${apiConfig.apiBaseURL}/getInvestmentScheduleStatus/${engagementId}`,
          {
            headers: {
              "x-api-key": apiConfig["x-api-key"],
              Authorization: `Bearer ${authToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        const { status, data, processingErrorFlag} = res.data;
        setStatus(status);
        console.log('newData 1', data)
        if (status === "Completed") {
          setIsPolling(false);
          setIsGenerating(false);
          setIsSubmitting(false);
          setShowLoadingPopup(false);
          setStatus("");
          clearInterval(pollingInterval);
          toast.success("Investment schedule analysis completed successfully!");
          navigate(`/investment-schedules/details/${id}`);
        } else if (status === "In Progress") {
          setStatus("In Progress");
          setIsPolling(true);
          // Keep existing isGenerating state from form submission

          // Start polling
          pollingInterval = setInterval(async () => {
            try {
              const pollRes = await axios.get(
                `${apiConfig.apiBaseURL}/getInvestmentScheduleStatus/${engagementId}`,
                {
                  headers: {
                    "x-api-key": apiConfig["x-api-key"],
                    Authorization: `Bearer ${authToken}`,
                    "Content-Type": "application/json",
                  },
                }
              );

              const { status: newStatus, data: newData, processingErrorFlag } = pollRes.data;
              if (processingErrorFlag) {
                clearInterval(pollingInterval);
                setIsPolling(false);
                setIsGenerating(false);
                setIsSubmitting(false);
                setShowLoadingPopup(false);
                setStatus("");
                toast.error("Processing encountered an error. Please try again.");
                return;
              }
              console.log('newData', newData)
              if (newStatus === "Completed") {
                clearInterval(pollingInterval);
                setIsPolling(false);
                setIsGenerating(false);
                setIsSubmitting(false);
                setShowLoadingPopup(false);
                setStatus("");
                toast.success("Investment schedule analysis completed successfully!");
                navigate(`/investment-schedules/details/${id}`);
              } else if (newStatus === "failed") {
                clearInterval(pollingInterval);
                setIsPolling(false);
                setIsGenerating(false);
                setIsSubmitting(false);
                setShowLoadingPopup(false);
                setStatus("");
                toast.error("Investment schedule analysis failed. Please try again");
              }
            } catch (pollError) {
              console.error("[Polling] Error:", pollError);
              clearInterval(pollingInterval);
              setIsPolling(false);
              setIsGenerating(false);
              setIsSubmitting(false);
              setShowLoadingPopup(false);
            }
          }, 5000);
        }
      } catch (error) {
        console.error("Initial fetch error:", error);
        setIsPolling(false);
        setIsGenerating(false);
        setIsSubmitting(false);
        setShowLoadingPopup(false);
      }
    };

    fetchStatusAndProcess();

    return () => {
      clearInterval(pollingInterval);
      setIsPolling(false);
    };
  }, [engagementId, navigate]);

  const getMessage = () => {
    if (currentMessageIndex < initialMessages.length) {
      return initialMessages[currentMessageIndex];
    } else {
      const loopIndex = (currentMessageIndex - initialMessages.length) % repeatMessages.length;
      return repeatMessages[loopIndex];
    }
  };

  const fetchClients = async () => {
    setClientsLoading(true);
    setClientsError(null);
    try {
      const authToken = localStorage.getItem("authToken");
      const response = await axios.get(
        `${apiConfig.apiBaseURL}/getAllClients`,
        {
          headers: {
            "x-api-key": apiConfig["x-api-key"],
            "Authorization": `Bearer ${authToken}`
          }
        }
      );

      if (response.data && response.data.success) {
        setClients(response.data.clients);
      } else {
        setClientsError(response.data.error);
        toast.error(response.data.error);
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
      setClientsError("Error fetching clients: " + (error.message || "Unknown error"));
      toast.error("Failed to load client list");
    } finally {
      setClientsLoading(false);
    }
  };

  const fetchInvestmentSchedule = async () => {
    try {
      setIsLoading(true);
      const authToken = localStorage.getItem("authToken");

      // First get the investment schedule details
      const response = await axios.get(
        `${apiConfig.apiBaseURL}/getInvestmentSchedule/${id}`,
        {
          headers: {
            "x-api-key": apiConfig["x-api-key"],
            "Authorization": `Bearer ${authToken}`
          }
        }
      );

      if (response.data && response.data.success) {
        console.log(response.data);
        const schedule = response.data.investmentSchedule;
        setFormData({
          clientName: schedule.clientId || '',
          analysisYear: schedule.analysisYear || '',
          currentYearInvestmentSchedule: null, // Cannot populate file inputs with URLs
          priorYearInvestmentSchedule: null,
          priorYearFinancials: null
        });

        // Store existing file URLs separately
        setExistingFiles({
          currentYearInvestmentSchedule: schedule.currentYearInvestmentSchedule || null,
          priorYearInvestmentSchedule: schedule.priorYearInvestmentSchedule || null,
          priorYearFinancials: schedule.priorYearFinancials || null
        });
      } else {
        toast.error("Failed to load investment schedule data");
        navigate('/investment-schedules');
      }
    } catch (error) {
      console.error("Error fetching investment schedule:", error);
      toast.error("Failed to load investment schedule data");
      navigate('/investment-schedules');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.clientName.trim()) {
      newErrors.clientName = 'Client name is required';
    }

    if (!formData.analysisYear) {
      newErrors.analysisYear = 'Analysis year is required';
    }

    // Check if there's a new file upload OR existing file (and not removed)
    const hasCurrentFile = formData.currentYearInvestmentSchedule && formData.currentYearInvestmentSchedule !== "";
    const hasExistingFile = existingFiles.currentYearInvestmentSchedule && !removedFiles.currentYearInvestmentSchedule;

    if (!hasCurrentFile && !hasExistingFile) {
      newErrors.currentYearInvestmentSchedule = 'Current year investment schedule is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleGenerateAnalysis = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fill all required fields before generating analysis");
      return;
    }

    setIsSubmitting(true);
    setIsGenerating(true);
    setShowLoadingPopup(true);

    try {
      const authToken = localStorage.getItem("authToken");

      if (!authToken) {
        toast.error("Authentication token not found. Please login again.");
        setIsSubmitting(false);
        return;
      }

      // Create FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('clientId', formData.clientName);
      formDataToSend.append('analysisYear', formData.analysisYear);
      formDataToSend.append('mode', 'map');
      formDataToSend.append('operation', 'update');
      formDataToSend.append('id', id);



      // Append current year file (new OR existing)
      if (formData.currentYearInvestmentSchedule && formData.currentYearInvestmentSchedule !== "") {
        formDataToSend.append('currentYearInvestmentSchedule', formData.currentYearInvestmentSchedule);
      } else if (existingFiles.currentYearInvestmentSchedule && !removedFiles.currentYearInvestmentSchedule) {
        formDataToSend.append('currentYearInvestmentSchedule', existingFiles.currentYearInvestmentSchedule);
      }

      // Append prior year file
      if (formData.priorYearInvestmentSchedule && formData.priorYearInvestmentSchedule !== "") {
        formDataToSend.append('priorYearInvestmentSchedule', formData.priorYearInvestmentSchedule);
      } else if (existingFiles.priorYearInvestmentSchedule && !removedFiles.priorYearInvestmentSchedule) {
        formDataToSend.append('priorYearInvestmentSchedule', existingFiles.priorYearInvestmentSchedule);
      }

      // Append prior year financials
      if (formData.priorYearFinancials && formData.priorYearFinancials !== "") {
        formDataToSend.append('priorYearFinancials', formData.priorYearFinancials);
      } else if (existingFiles.priorYearFinancials && !removedFiles.priorYearFinancials) {
        formDataToSend.append('priorYearFinancials', existingFiles.priorYearFinancials);
      }


      // Make API call to update investment schedule mapping
      const response = await axios.post(
        `${apiConfig.apiBaseURL}/investmentScheduleMapping`,
        formDataToSend,
        {
          headers: {
            "x-api-key": apiConfig["x-api-key"],
            "Authorization": `Bearer ${authToken}`,
            "Content-Type": "multipart/form-data"
          }
        }
      );

      // Handle successful response
      if (response.data && response.data.success) {
        // If response includes engagementId, start polling
        if (response.data.id) {
          // Don't change any loading states, just set the engagement ID
          // This will trigger the polling useEffect but keep the same popup open
          setEngagementId(response.data.id);
          return; // Exit early to prevent cleanup
        } else {
          toast.success(response.data.message || 'Investment schedule analysis generated successfully!');
          console.log('Investment Schedule Analysis Response:', response.data);
          // If there's an engagementId in response, redirect to details using that, otherwise use current id
          if (response.data.engagementId) {
            navigate(`/investment-schedules/details/${response.data.engagementId}`);
          } else {
            navigate(`/investment-schedules/details/${id}`);
          }
        }
      } else {
        // Handle API error response
        const errorMessage = response.data?.error || response.data?.message || 'Failed to generate investment schedule analysis';
        toast.error(errorMessage);
        console.error('API Error Response:', response.data);
      }

    } catch (error) {
      console.error('Error generating investment schedule analysis:', error.response?.data?.message);
      toast.error(error.response?.data?.message || "An error occurred");
      // Reset loading states on error
      setIsGenerating(false);
      setShowLoadingPopup(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveAndExit = async () => {
    if (!validateForm()) {
      toast.error("Please fill all required fields before saving");
      return;
    }

    setIsSubmitting(true);

    try {
      const authToken = localStorage.getItem("authToken");

      if (!authToken) {
        toast.error("Authentication token not found. Please login again.");
        setIsSubmitting(false);
        return;
      }

      // Create FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('clientId', formData.clientName);
      formDataToSend.append('analysisYear', formData.analysisYear);
      formDataToSend.append('mode', 'save');
      formDataToSend.append('operation', 'update');
      formDataToSend.append('id', id);

      // Append current year file (new OR existing)
      if (formData.currentYearInvestmentSchedule && formData.currentYearInvestmentSchedule !== "") {
        formDataToSend.append('currentYearInvestmentSchedule', formData.currentYearInvestmentSchedule);
      } else if (existingFiles.currentYearInvestmentSchedule && !removedFiles.currentYearInvestmentSchedule) {
        formDataToSend.append('currentYearInvestmentSchedule', existingFiles.currentYearInvestmentSchedule);
      }

      // Append prior year file
      if (formData.priorYearInvestmentSchedule && formData.priorYearInvestmentSchedule !== "") {
        formDataToSend.append('priorYearInvestmentSchedule', formData.priorYearInvestmentSchedule);
      } else if (existingFiles.priorYearInvestmentSchedule && !removedFiles.priorYearInvestmentSchedule) {
        formDataToSend.append('priorYearInvestmentSchedule', existingFiles.priorYearInvestmentSchedule);
      }

      // Append prior year financials
      if (formData.priorYearFinancials && formData.priorYearFinancials !== "") {
        formDataToSend.append('priorYearFinancials', formData.priorYearFinancials);
      } else if (existingFiles.priorYearFinancials && !removedFiles.priorYearFinancials) {
        formDataToSend.append('priorYearFinancials', existingFiles.priorYearFinancials);
      }


      // Make API call to update investment schedule
      const response = await axios.post(
        `${apiConfig.apiBaseURL}/investmentScheduleMapping`,
        formDataToSend,
        {
          headers: {
            "x-api-key": apiConfig["x-api-key"],
            "Authorization": `Bearer ${authToken}`,
            "Content-Type": "multipart/form-data"
          }
        }
      );

      // Handle successful response
      if (response.data && response.data.success) {
        toast.success(response.data.message || 'Investment schedule saved successfully!');
        console.log('Investment Schedule Save Response:', response.data);

        // Navigate back to investment schedules page
        navigate('/investment-schedules');
      } else {
        // Handle API error response
        const errorMessage = response.data?.error || response.data?.message || 'Failed to save investment schedule';
        toast.error(errorMessage);
        console.error('API Error Response:', response.data);
      }

    } catch (error) {
      console.error('Error saving investment schedule:', error.response?.data?.message);
      toast.error(error.response?.data?.message || "An error occurred while saving");
    } finally {
      setIsSubmitting(false);
    }
  };

  const mockFormik = {
    values: formData,
    setFieldValue: (name, value) => {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));

      // Track when files are removed
      if (value === "" && existingFiles[name]) {
        setRemovedFiles(prev => ({
          ...prev,
          [name]: true
        }));
      } else if (value && value !== "") {
        setRemovedFiles(prev => ({
          ...prev,
          [name]: false
        }));
      }

      // Clear error when file is selected
      if (errors[name]) {
        setErrors(prev => ({
          ...prev,
          [name]: ''
        }));
      }
    },
    errors,
    touched: {
      currentYearInvestmentSchedule: true,
      priorYearInvestmentSchedule: true,
      priorYearFinancials: true
    },
    setFieldTouched: () => { }
  };

  if (isLoading) {
    return (
      <Container fluid className="py-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Container>
    );
  }

  if (showLoadingPopup) {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          backgroundColor: "rgba(0, 0, 0, 0.3)",
          zIndex: 1050,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            width: "420px",
            backgroundColor: "#fff",
            borderRadius: "16px",
            padding: "40px 20px",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
            textAlign: "center",
          }}
        >
          <img
            src={image}
            alt="Processing"
            style={{
              width: "120px",
              marginBottom: "20px",
            }}
          />
          <h5 style={{ fontWeight: 500, fontSize: "18px", color: "#333" }}>
            The AI is working on it.
          </h5>

          <div className='d-flex justify-content-center'>
            <ReactTextTransition>
              {getMessage()}
            </ReactTextTransition>
          </div>
          {extraMessage && (
            <p style={{ fontSize: "14px", color: "#999", marginTop: "10px" }}>
              {extraMessage}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <Container fluid className="py-4">
      <Card className="shadow-sm border-0">
        <Card.Body className="p-4">
          <Form onSubmit={handleGenerateAnalysis} className="custom-inputs">
            <Container fluid className="mx-0">
              <Card className="py-3" style={{ border: "2px solid #ECEEF5" }}>
                <Row className="px-4 py-2">
                  <h5 className="fw-bold text-dark mb-4">Edit Investment Analysis</h5>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Client Name*</Form.Label>
                      <Form.Select
                        name="clientName"
                        value={formData.clientName}
                        onChange={handleInputChange}
                        isInvalid={!!errors.clientName}
                        className="p-3"
                        style={{ backgroundColor: "#E7EFF8", borderRadius: "8px" }}
                        disabled={clientsLoading}
                      >
                        <option value="">
                          {clientsLoading ? "Loading clients..." : "Select Client"}
                        </option>
                        {clients.map(client => (
                          <option
                            key={client._id}
                            value={client._id}
                          >
                            {client.clientName}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.clientName}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Analysis Year*</Form.Label>
                      <Form.Select
                        name="analysisYear"
                        value={formData.analysisYear}
                        onChange={handleInputChange}
                        isInvalid={!!errors.analysisYear}
                        className="p-3"
                        style={{ backgroundColor: "#E7EFF8", borderRadius: "8px" }}
                      >
                        <option value="">Select Year</option>
                        {analysisYears.map((year, index) => (
                          <option key={index} value={year}>{year}</option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.analysisYear}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <hr />

                <Row className="px-4 pt-2">
                  <h5 className="fw-bold text-dark mb-4">Required Documents</h5>
                  <Col lg={4} className="d-flex">
                    <div className="w-100" style={{ minHeight: '140px' }}>
                      <DocumentUpload
                        formik={mockFormik}
                        name="currentYearInvestmentSchedule"
                        acceptedTypes={{
                          "application/vnd.ms-excel": [],
                          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
                          "text/csv": [],
                        }}
                        label="Current Year Investment Schedule*"
                        fileUrl={existingFiles.currentYearInvestmentSchedule}
                        required={true}
                      />
                    </div>
                    <DownloadSampleFile fileName="Investment_Schedule_Sample.csv" displayText="Download Investment Schedule Sample" />
                  </Col>
                  <Col lg={4} className="d-flex">
                    <div className="w-100" style={{ minHeight: '140px' }}>
                      <DocumentUpload
                        formik={mockFormik}
                        name="priorYearInvestmentSchedule"
                        acceptedTypes={{
                          "application/vnd.ms-excel": [],
                          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
                          "text/csv": [],
                        }}
                        label="Prior Year Investment Schedule (Optional)"
                        fileUrl={existingFiles.priorYearInvestmentSchedule}
                      />
                    </div>
                    <DownloadSampleFile fileName="Investment_Schedule_Sample.csv" displayText="Download Investment Schedule Sample" />
                  </Col>
                  <Col lg={4} className="d-flex">
                    <div className="w-100" style={{ minHeight: '140px' }}>
                      <DocumentUpload
                        formik={mockFormik}
                        name="priorYearFinancials"
                        acceptedTypes={{
                          "application/pdf": [],
                          "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [],
                        }}
                        label="Prior Year Financials Statements (Optional)"
                        fileUrl={existingFiles.priorYearFinancials}
                      />
                    </div>
                  </Col>
                </Row>
              </Card>
            </Container>

            <Container fluid className="px-0 setup-footer">
              <Card
                className="h-100 py-4 px-3 mt-3"
                style={{ border: "2px solid #ECEEF5" }}
              >
                <div className="d-flex justify-content-end mt-3 all-buttons">
                  <Button
                    variant="outline-primary"
                    className="px-5 py-3 me-3"
                    onClick={handleSaveAndExit}
                    disabled={isSubmitting || isGenerating}
                  >
                    Save & Exit
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    className="px-5 py-3"
                    disabled={isSubmitting || isGenerating}
                    style={{ backgroundColor: '#1e3a8a', borderColor: '#1e3a8a' }}
                  >
                    {isSubmitting || isGenerating ? 'Generating...' : 'Generate Analysis Report'}
                  </Button>
                </div>
              </Card>
            </Container>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
}

export default EditAnalysisPage;